import type { DrawingDocument, NormalizedPoint } from '../model';
import type {
  MarkushDrawingDocument,
  MarkushFragment,
  MarkushScaffold,
  PlaceholderPosition
} from './markushDocument.ts';
import { elementSymbol } from '../chemistry/elements.ts';

// #587 M2: projections between the Markush document and the editor's v10 DrawingDocument, so the
// existing atom/bond mutations, projections and renderers work on a scaffold or R-group member
// unchanged (ADR 0013 § Rollout 2: "a Fragment is structurally a mini-DrawingDocument"). The
// scaffold's placeholder nodes survive the round trip as graph nodes with a coordinate but no
// atoms entry -- documentToAtoms already skips those, and deleteAtom leaves them alone.

const SCAFFOLD_ID = 'markush-scaffold';

/** Wraps a scaffold or fragment as a v10 DrawingDocument. Stable ids (default
 *  `markush-scaffold`), empty stereochemistry and depiction extras: the v11 Markush shapes carry
 *  none of them. */
export function markushStructureToDrawingDocument(
  structure: MarkushScaffold,
  id: string = SCAFFOLD_ID
): DrawingDocument {
  const { coordinateSet } = structure;
  return {
    schemaVersion: 'chem-drawing.v10',
    structure: {
      id,
      revision: 1,
      graph: { id, revision: 1, nodes: structure.graph.nodes, edges: structure.graph.edges },
      atoms: structure.atoms,
      bonds: structure.bonds,
      formalCharges: structure.formalCharges,
      stereochemistry: { tetrahedral: {}, doubleBonds: {} }
    },
    depiction: {
      id: `${id}-depiction`,
      revision: 1,
      structureId: id,
      structureRevision: 1,
      coordinateSet: {
        id: `${id}-coords`,
        kind: 'depiction-2d',
        structureId: id,
        structureRevision: 1,
        dimensions: 2,
        units: 'drawing-unit',
        width: coordinateSet.width,
        height: coordinateSet.height,
        coordinates: coordinateSet.coordinates
      },
      atomLabels: {},
      bondStyles: {},
      bondLengthOverrides: {}
    },
    annotations: [],
    derivedPropertySets: []
  };
}

/** The inverse of markushStructureToDrawingDocument. Drops what the v11 Markush shapes cannot
 *  hold: stereochemistry, atom labels, bond styles/length overrides, annotations and perception
 *  results. */
export function drawingDocumentToMarkushStructure(document: DrawingDocument): MarkushScaffold {
  const { structure, depiction } = document;
  return {
    graph: { nodes: structure.graph.nodes, edges: structure.graph.edges },
    atoms: structure.atoms,
    bonds: structure.bonds,
    formalCharges: structure.formalCharges,
    coordinateSet: {
      width: depiction.coordinateSet.width,
      height: depiction.coordinateSet.height,
      coordinates: depiction.coordinateSet.coordinates
    }
  };
}

export function scaffoldToDrawingDocument(document: MarkushDrawingDocument): DrawingDocument {
  const drawing = markushStructureToDrawingDocument(document.scaffold);
  // #772 -- the page travels with the drawing.
  return document.page ? { ...drawing, page: document.page } : drawing;
}

export function fragmentToDrawingDocument(fragment: MarkushFragment, id = 'markush-fragment'): DrawingDocument {
  return markushStructureToDrawingDocument(fragment, id);
}

export function drawingDocumentToFragment(document: DrawingDocument, openValenceAtomId: string): MarkushFragment {
  return { ...drawingDocumentToMarkushStructure(document), openValenceAtomId };
}

export interface MarkushPositionMarker {
  readonly atomId: string;
  readonly kind: 'r-group' | 'attachment-point' | 'atom-list';
  /** Display text: the R-label, or the atom list as `[C,N,O,S]`. */
  readonly label: string;
  readonly position: NormalizedPoint;
}

export interface MarkushRepeatUnitMarker {
  readonly atomIds: readonly string[];
  readonly bondIds: readonly string[];
  /** Bracket subscript: `n=1–6`, `n=2` or `n≥1`. */
  readonly countLabel: string;
}

function repeatCountLabel(countMin: number, countMax: number | undefined): string {
  if (countMax === undefined) return `n≥${countMin}`;
  return countMin === countMax ? `n=${countMin}` : `n=${countMin}–${countMax}`;
}

function markerLabel(position: PlaceholderPosition): string {
  if (position.kind === 'atom-list') return `[${position.allowedAtomicNumbers.map(elementSymbol).join(',')}]`;
  return position.rGroupLabel;
}

/** One display marker per placeholder node (the documentToAtoms counterpart for the nodes it
 *  skips), positioned in the same normalized space. */
export function markushPositionMarkers(document: MarkushDrawingDocument): readonly MarkushPositionMarker[] {
  const { coordinateSet } = document.scaffold;
  return document.variablePositions.flatMap((position) => {
    if (position.kind === 'repeat-unit') return [];
    const coordinate = coordinateSet.coordinates[position.atomId];
    if (!coordinate) return [];
    return [
      {
        atomId: position.atomId,
        kind: position.kind,
        label: markerLabel(position),
        position: { x: coordinate.x / coordinateSet.width, y: coordinate.y / coordinateSet.height }
      }
    ];
  });
}

export function markushRepeatUnitMarkers(document: MarkushDrawingDocument): readonly MarkushRepeatUnitMarker[] {
  return document.variablePositions.flatMap((position) =>
    position.kind === 'repeat-unit'
      ? [
          {
            atomIds: position.fragmentAtomIds,
            bondIds: position.fragmentBondIds,
            countLabel: repeatCountLabel(position.countMin, position.countMax)
          }
        ]
      : []
  );
}
